import { Button } from '@helix-ds/react'
import { Close, Pen } from 'flowbite-react-icons/outline'
import {
  SECTION_LABELS,
  SEND_UPDATES_SECTION_SHORT_LABELS,
  type SendOrderUpdatesSectionId,
} from '@/components/modals/sendOrderUpdatesSections'

export type { SendOrderUpdatesSectionId } from '@/components/modals/sendOrderUpdatesSections'

export interface SendOrderUpdatesConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  /** Persist updates (runs after user confirms). */
  onConfirm: () => void | Promise<void>
  /** Which sections changed — drives the bullet list. Order matches Figma when all three clinical paths are dirty. */
  updatedSections: SendOrderUpdatesSectionId[]
  /**
   * Display labels for comparators the user removed this session that will be
   * cancelled (removed from the order) when the update is sent. Rendered as
   * their own bullets below the updated-section list (e.g. "Mother"). Dynamic
   * per-order, so they are passed as resolved strings rather than section ids.
   */
  cancelledComparatorLabels?: string[]
  isSubmitting?: boolean
  /**
   * When true, this Send Updates session was started as a reanalysis request
   * (arrived at the in-progress page via the results-page "Request Reanalysis"
   * action / `?reanalysis=true`). Switches the confirm copy to reanalysis-
   * specific wording so the user knows the update triggers a reanalysis.
   */
  isReanalysis?: boolean
  /**
   * When true, sending this update will trigger a reanalysis of the order. The
   * parent infers this from what changed (clinical info changed and no comparator
   * was added). Switches the confirm copy to reanalysis-specific wording so the
   * user knows the update triggers a reanalysis.
   */
  willReanalyze?: boolean
  /**
   * Explanatory note shown when the edits WOULD trigger a reanalysis but the gate
   * blocks it (a reanalysis is already in flight, or the org's per-order cap has
   * been reached). The update is still sent, just without the reanalysis flag.
   */
  reanalysisBlockedNote?: string
  /**
   * Whether these updates could re-issue the requisition for signature — true
   * for an org that signs its requisitions in the app. Replaces the bare "sent
   * to lab after you confirm" line with the step it skips: the lab does not see
   * the updates until the refreshed form is signed.
   *
   * Only *could*. Whether an edit is material enough to re-issue is decided by
   * the server, from the rendered form, after this modal is confirmed, so the
   * copy is conditional rather than a promise. Guessing here would mean
   * reimplementing that rule against a list of touched sections and telling the
   * clinician to expect a signature request that never arrives.
   */
  mayRequireNewSignature?: boolean
}

export function SendOrderUpdatesConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  updatedSections,
  cancelledComparatorLabels = [],
  isSubmitting = false,
  isReanalysis = false,
  willReanalyze = false,
  reanalysisBlockedNote,
  mayRequireNewSignature = false,
}: SendOrderUpdatesConfirmModalProps) {
  if (!isOpen) {
    return null
  }

  const hasBullets = updatedSections.length > 0 || cancelledComparatorLabels.length > 0

  return (
    <div className="fixed inset-0 z-popover flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={() => {
          if (!isSubmitting) onClose()
        }}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      <div
        className="relative w-full max-w-md rounded-base border border-border-base bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-order-updates-title"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close"
          className="absolute right-3 top-3 flex size-9 items-center justify-center rounded-full text-text-body transition-colors hover:bg-background-gray hover:text-text-heading focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
        >
          <Close className="h-5 w-5" />
        </button>

        <div className="flex flex-col items-center gap-4 pt-1">
          <div className="flex size-12 shrink-0 items-center justify-center text-text-fg-brand dark:text-brand-400" aria-hidden="true">
            <Pen className="h-10 w-10" />
          </div>

          <div className="w-full max-w-[21rem] text-center text-text-body dark:text-gray-400">
            <p
              id="send-order-updates-title"
              className="text-lg font-semibold tracking-tight text-text-heading dark:text-white"
            >
              {willReanalyze ? 'Request reanalysis?' : 'Do you want to send these order updates?'}
            </p>

            {isReanalysis && hasBullets && (
              <p className="mt-4 text-left text-base font-medium leading-6 text-text-heading dark:text-white">
                These updates will be included:
              </p>
            )}

            {hasBullets && (
              <ul
                className={`${isReanalysis ? 'mt-2' : 'mt-4'} list-disc pl-5 text-left text-base font-medium leading-6 text-text-heading marker:text-text-body dark:text-white dark:marker:text-gray-400`}
              >
                {updatedSections.map((id) => (
                  <li key={id} className="pl-1">
                    {isReanalysis ? SEND_UPDATES_SECTION_SHORT_LABELS[id] : SECTION_LABELS[id]}
                  </li>
                ))}
                {cancelledComparatorLabels.map((label) => (
                  <li key={`cancel-${label}`} className="pl-1">
                    {label} will be removed from the order
                  </li>
                ))}
              </ul>
            )}

            {willReanalyze ? (
              <p className="mt-4 text-left text-base font-medium leading-6 text-text-body dark:text-gray-400">
                We’ll send this to the lab so they can reanalyze the order with the updated clinical
                information.
              </p>
            ) : mayRequireNewSignature ? (
              /* Set in a well at a smaller, lighter weight: it explains a
                 consequence rather than asking the question, and at the heading
                 size the sentence crowds out the title it sits under. */
              <p className="mt-4 rounded-base bg-background-gray p-3 text-left text-sm font-normal leading-5 text-text-body dark:bg-gray-800 dark:text-gray-400">
                If these updates change the requisition, it will need a new signature and older versions
                will be invalidated. The updates reach the lab once it’s signed.
              </p>
            ) : (
              <p className="mt-4 text-lg font-semibold tracking-tight text-text-heading dark:text-white">
                Updates will be sent to lab after you confirm.
              </p>
            )}

            {reanalysisBlockedNote && (
              <p className="mt-3 text-left text-sm font-medium leading-6 text-text-body-subtle dark:text-gray-500">
                {reanalysisBlockedNote}
              </p>
            )}
          </div>

          <div className="mt-2 flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-full border border-border-base bg-white px-4 py-2.5 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-background-gray disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[10rem] dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              No, don&apos;t send
            </button>
            <Button
              variant="primary"
              size="md"
              type="button"
              onClick={() => void onConfirm()}
              disabled={isSubmitting}
              className="min-h-[2.5rem] flex-1 sm:flex-initial sm:min-w-[10rem] focus-visible:ring-brand-600"
            >
              {isSubmitting ? 'Sending…' : willReanalyze ? 'Reanalyze' : isReanalysis ? 'Send updates' : 'Yes, update order'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
